import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { roomCardPrice, type RoomCardsAttachment, type RoomCard } from "../widget/room-cards";

@customElement("cg-room-cards")
export class CGRoomCards extends LitElement {
  @property({ attribute: false }) rooms?: RoomCardsAttachment;
  @property({ type: Boolean }) disabled = false;
  static override styles = css`
    :host { display:block; min-width:0; margin-top:12px; }
    .controls { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; font-size:12px; }
    .arrows { display:flex; gap:8px; }
    .track { display:flex; gap:12px; overflow-x:auto; scroll-snap-type:x mandatory; padding-bottom:8px; }
    article { flex:0 0 min(250px, 100%); scroll-snap-align:start; border:1px solid var(--cg-border); border-radius:10px; overflow:hidden; display:flex; flex-direction:column; background:var(--cg-surface); color:var(--cg-text); }
    img { display:block; width:100%; height:144px; object-fit:cover; }
    img[hidden] { display:none; }
    .body { display:flex; flex:1; flex-direction:column; gap:12px; padding:12px; }
    h3 { font-size:14px; font-weight:600; margin:0; }
    p { font-size:13px; line-height:1.5; margin:0; white-space:normal; }
    .price { font-size:14px; font-weight:600; color:var(--cg-text); }
    button { font:inherit; border:1px solid var(--cg-border); border-radius:8px; cursor:pointer; padding:6px 12px; background:var(--cg-surface); color:var(--cg-text); }
    .book { margin-top:auto; background:var(--cg-accent, var(--cg-primary)); color:var(--cg-on-primary, #fff); padding:10px 12px; font-size:14px; font-weight:500; }
    button:disabled { opacity:.5; cursor:not-allowed; }
    button:focus-visible, .track:focus-visible { outline:2px solid var(--cg-accent, var(--cg-primary)); outline-offset:2px; }
  `;
  private scrollRooms(direction: number) {
    this.renderRoot.querySelector(".track")?.scrollBy({ left: direction * 262, behavior: "smooth" });
  }
  private select(card: RoomCard) {
    this.dispatchEvent(new CustomEvent("room-select", { detail: { card }, bubbles: true, composed: true }));
  }
  private photo(url: string | undefined): string | undefined {
    try { const value = new URL(url ?? ""); return ["http:", "https:"].includes(value.protocol) && !value.username && !value.password ? value.href : undefined; } catch { return undefined; }
  }
  override render() {
    if (!this.rooms?.cards.length) return nothing;
    return html`<section aria-label="Rooms">
      ${this.rooms.cards.length > 1 ? html`<div class="controls"><span>Swipe to explore rooms</span><div class="arrows"><button aria-label="Previous room" @click=${() => this.scrollRooms(-1)}>‹</button><button aria-label="Next room" @click=${() => this.scrollRooms(1)}>›</button></div></div>` : nothing}
      <div class="track" role="list" tabindex="0" aria-label="Room cards">
        ${this.rooms.cards.map((card) => {
          const expired = !Number.isFinite(Date.parse(card.expiresAt)) || Date.parse(card.expiresAt) <= Date.now();
          const photo = this.photo(card.photo);
          return html`<article role="listitem">
            ${photo ? html`<img src=${photo} alt=${card.name} loading="lazy" decoding="async" referrerpolicy="no-referrer" @error=${(e: Event) => { (e.currentTarget as HTMLImageElement).hidden = true; }} />` : nothing}
            <div class="body"><h3>${card.name}</h3><p class="price">${roomCardPrice(card)}</p>${card.description ? html`<p>${card.description}</p>` : nothing}
              <button class="book" ?disabled=${this.disabled || expired} aria-label=${`Book Now: ${card.name}`} @click=${() => this.select(card)}>${expired ? "Ask for rooms again" : "Book Now"}</button>
            </div></article>`;
        })}
      </div>
    </section>`;
  }
}
